import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { solesStrACent } from "@huayruro/shared";
import { useApi, mutar } from "../lib/useApi";
import { ahoraFecha } from "../lib/fecha-ui";
import { solesCent } from "../lib/money";
import { useMasAngostoQue } from "../lib/pantalla";
import { cn, Card, Input, Button, Chip, EmptyState, TableHead, TableRow, Th, Td } from "../components/ui";
import type { SesionActiva } from "../lib/tipos";

// Cierre de caja del día (restyle al tema claro). Mismas llamadas (/caja/dia, POST /caja/cierres),
// mismos guards de cerrar (admin), mismo cálculo server (esperado + diferencia). Tramo 03:
//  · el dueño elige la botica (antes veía «super_admin debe indicar sucursal_id») y MIRA: la caja la
//    cierra el encargado de cada botica (K-2 por defecto; cerrar la de cualquier botica sería ampliar).
//  · «Cerrar caja» muestra un resumen y pide confirmar: el cierre no se puede deshacer (OA-2).
//  · en el celular los cierres van en tarjetas: la tabla de 5 columnas cortaba el resultado.

type ResumenDia = { fecha: string; por_metodo: Record<string, number>; total_sistema_cent: number; num_ventas: number };
type Cierre = { id: string; fecha: string; total_efectivo_cent: number; total_yape_cent: number; total_otros_cent: number; total_sistema_cent: number; diferencia_cent: number; cerrado_at: string };

const aCent = (s: string): number => {
  const t = s.trim().replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return 0;
  try {
    return solesStrACent(t);
  } catch {
    return 0;
  }
};

const METODO_LABEL: Record<string, string> = { efectivo: "Efectivo", yape: "Yape", plin: "Plin", tarjeta: "Tarjeta", transferencia: "Transferencia", otro: "Otro" };

const COLS = "110px 1fr 1fr 1fr 150px";

// Resultado del cierre (chip). Sin link "ver caso": esta vista la usa también el vendedor (POS) y Casos es admin+.
function resultadoCierre(dif: number): ReactNode {
  if (dif === 0) return <Chip variant="ok">Cuadró</Chip>;
  if (dif > 0) return <Chip variant="warn">Sobró {solesCent(dif)}</Chip>;
  return <Chip variant="danger">Faltó {solesCent(Math.abs(dif))}</Chip>;
}

function fechaCorta(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return y && m && d ? `${d}/${m}/${y}` : ymd;
}

export function Caja({ sesion }: { sesion: SesionActiva }) {
  const rol = sesion.usuario.rol;
  const esSuper = rol === "super_admin";
  const [fecha, setFecha] = useState(ahoraFecha());
  // Cierra quien manda en UNA botica. El dueño mira la de la botica que elija (tramo 03, K-2).
  const puedeCerrar = rol === "admin_sucursal";

  // El dueño no tiene botica fija: elige una, como en Inventario (la primera por defecto).
  const sucursales = useApi<{ sucursales: { id: string; nombre: string }[] }>(esSuper ? "/sucursales" : null);
  const [sucId, setSucId] = useState("");
  useEffect(() => {
    const primera = sucursales.data?.sucursales[0]?.id;
    if (esSuper && !sucId && primera) setSucId(primera);
  }, [esSuper, sucId, sucursales.data]);
  const listo = !esSuper || !!sucId;
  const q = esSuper && sucId ? `&sucursal_id=${sucId}` : "";

  const dia = useApi<ResumenDia>(listo ? `/caja/dia?fecha=${fecha}${q}` : null, [fecha, sucId]);
  const historial = useApi<{ cierres: Cierre[] }>(listo ? `/caja/cierres${q ? `?${q.slice(1)}` : ""}` : null, [sucId]);
  const tarjetas = useMasAngostoQue(640);

  const [efectivo, setEfectivo] = useState("");
  const [yape, setYape] = useState("");
  const [otros, setOtros] = useState("");
  const [obs, setObs] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; texto: string } | null>(null);

  const contadoCent = aCent(efectivo) + aCent(yape) + aCent(otros);
  const sistemaCent = dia.data?.total_sistema_cent ?? 0;
  const difPreview = contadoCent - sistemaCent;
  const hoy = ahoraFecha();

  async function cerrar() {
    setConfirmando(false);
    setEnviando(true);
    setMsg(null);
    try {
      const r = await mutar<{ diferencia_cent: number }>("/caja/cierres", {
        method: "POST",
        body: { fecha, total_efectivo_cent: aCent(efectivo), total_yape_cent: aCent(yape), total_otros_cent: aCent(otros), observaciones: obs.trim() || null },
      });
      setMsg({ ok: true, texto: `Caja cerrada. Diferencia: ${solesCent(r.diferencia_cent)}` });
      setEfectivo("");
      setYape("");
      setOtros("");
      setObs("");
      historial.recargar();
    } catch (e) {
      setMsg({ ok: false, texto: e instanceof Error ? e.message : String(e) });
    } finally {
      setEnviando(false);
    }
  }

  const cierres = historial.data?.cierres ?? [];
  // Un día ya cerrado no ofrece cerrarse otra vez (tramo 04, Kevin): el server lo rechaza con 409
  // (UNIQUE botica+fecha) y el formulario prometía algo imposible. El historial trae los últimos 60
  // días; para una fecha más vieja queda el formulario y el 409 del server con su motivo.
  const cierreDelDia = cierres.find((c) => c.fecha === fecha);
  const historialListo = !!historial.data || !!historial.error;
  // «Falta» y «Sobra» (plata) y no «Faltante»: «Faltante» es desde el tramo 03 el producto que pidieron
  // y no había (CH-08). «Cuadró», «Sobró» y «Faltó» de la tabla no cambian.
  const etiquetaDif = difPreview >= 0 ? "Sobra" : "Falta";

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-[18px]">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-[16.5px] font-bold tracking-[-0.01em] text-ink">Caja</h1>
        <div className="flex flex-wrap items-center gap-2">
          {esSuper && sucursales.data && (
            <label className="flex items-center gap-2 text-[12px] text-ink-2">
              Botica:
              <select
                value={sucId}
                onChange={(e) => setSucId(e.target.value)}
                className="min-h-[40px] rounded-[9px] border border-line-input bg-field px-3 py-1.5 text-[13px] text-ink outline-none"
              >
                {sucursales.data.sucursales.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nombre}
                  </option>
                ))}
              </select>
            </label>
          )}
          {/* Envuelto con ancho fijo: `Input` trae `w-full` y `cn` solo junta clases (un `w-auto` no le
              gana), así que junto al selector del dueño la fecha se estiraba a todo el ancho. */}
          <div className="w-[172px]">
            <Input type="date" max={hoy} value={fecha} onChange={(e) => setFecha(e.target.value)} className="min-h-[40px] text-[13px]" />
          </div>
        </div>
      </div>
      {esSuper && <p className="-mt-2 text-[12.5px] text-ink-2">Aquí ves la caja de cada botica. La cierra su encargado, al final del día.</p>}

      {/* Resumen del día (sistema) */}
      <Card>
        <h2 className="mb-2 text-[13.5px] font-bold text-ink">Resumen del día</h2>
        {dia.error ? (
          <div className="py-4 text-center">
            <p className="text-[13px] text-accent-ink">{dia.error}</p>
            <button type="button" onClick={dia.recargar} className="mt-2 min-h-[44px] px-3 text-[12.5px] font-medium text-link hover:text-link-hover hover:underline">
              Reintentar
            </button>
          </div>
        ) : !listo || dia.cargando || !dia.data ? (
          // `!dia.data` también: cuando el dueño recién elige botica, hay un render en que la dirección ya
          // existe y la carga todavía no empezó (useApi arranca en el efecto). Sin esto, pantalla en blanco.
          <p className="py-4 text-center text-[13px] text-ink-3">Cargando caja…</p>
        ) : (
          <>
            <p className="text-[12.5px] text-ink-2">{dia.data.num_ventas} venta(s) completadas</p>
            <ul className="mt-2 flex flex-col gap-1">
              {Object.entries(dia.data.por_metodo).map(([m, cent]) => (
                <li key={m} className="flex justify-between text-[13px]">
                  <span className="text-ink-2">{METODO_LABEL[m] ?? m}</span>
                  <span className="tabular-nums text-ink">{solesCent(cent)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-between border-t border-line-row pt-3 text-[13px] font-bold">
              <span className="text-ink">Total sistema</span>
              <span className="tabular-nums text-ink">{solesCent(sistemaCent)}</span>
            </div>
          </>
        )}
      </Card>

      {/* El día elegido ya tiene cierre: se muestra el resultado, no el formulario. */}
      {puedeCerrar && cierreDelDia && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-[13.5px] font-bold text-ink">
              Caja del {fechaCorta(fecha)}
              {fecha === hoy ? " (hoy)" : ""}: ya está cerrada
            </h2>
            {resultadoCierre(cierreDelDia.diferencia_cent)}
          </div>
          <p className="mt-1 text-[13px] text-ink-2 tabular-nums">
            Esperado {solesCent(cierreDelDia.total_sistema_cent)} · contado{" "}
            {solesCent(cierreDelDia.total_efectivo_cent + cierreDelDia.total_yape_cent + cierreDelDia.total_otros_cent)}
          </p>
          <p className="mt-1 text-[12px] text-ink-3">Un cierre no se puede deshacer. Para cerrar otro día, cambia la fecha de arriba.</p>
          {msg && <p className={cn("mt-2 text-[13px]", msg.ok ? "text-ok" : "text-accent-ink")}>{msg.texto}</p>}
        </Card>
      )}

      {/* Cerrar caja del día (encargado de la botica) */}
      {puedeCerrar && historialListo && !cierreDelDia && (
        <Card>
          <h2 className="mb-3 text-[13.5px] font-bold text-ink">Cerrar caja del día</h2>
          <div className="grid grid-cols-3 gap-2">
            <Campo label="Efectivo" value={efectivo} onChange={setEfectivo} />
            <Campo label="Yape/Plin" value={yape} onChange={setYape} />
            <Campo label="Otros" value={otros} onChange={setOtros} />
          </div>
          <Input className="mt-3" value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observaciones (opcional)" />
          <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[13px]">
            <span className="text-ink-2">
              Contado <span className="tabular-nums">{solesCent(contadoCent)}</span> · Sistema <span className="tabular-nums">{solesCent(sistemaCent)}</span>
            </span>
            <span className={cn("font-semibold tabular-nums", difPreview === 0 ? "text-ink-3" : difPreview > 0 ? "text-ok" : "text-accent-ink")}>
              {etiquetaDif}: {solesCent(Math.abs(difPreview))}
            </span>
          </div>
          {confirmando ? (
            <div className="mt-3 rounded-[11px] border border-line-input bg-inset p-3" role="alertdialog" aria-label="Confirmar el cierre">
              <p className="text-[13.5px] font-semibold text-ink">¿Cierras la caja del {fechaCorta(fecha)}{fecha === hoy ? " (hoy)" : ""}?</p>
              <p className="mt-1 text-[13px] text-ink-2 tabular-nums">
                Contado {solesCent(contadoCent)} · Sistema {solesCent(sistemaCent)} ·{" "}
                <span className={cn("font-semibold", difPreview === 0 ? "text-ink" : difPreview > 0 ? "text-ok" : "text-accent-ink")}>
                  {difPreview === 0 ? "cuadra" : `${etiquetaDif.toLowerCase()} ${solesCent(Math.abs(difPreview))}`}
                </span>
              </p>
              <p className="mt-1 text-[12px] text-ink-3">Un cierre no se puede deshacer.</p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => setConfirmando(false)} className="min-h-[44px] justify-center">
                  Volver
                </Button>
                <Button variant="primary" onClick={() => void cerrar()} className="min-h-[44px] justify-center">
                  Sí, cerrar la caja
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="primary" onClick={() => setConfirmando(true)} disabled={enviando || !dia.data} className="mt-3 min-h-[44px] w-full justify-center">
              {enviando ? "Cerrando…" : "Cerrar caja"}
            </Button>
          )}
          {msg && <p className={cn("mt-2 text-[13px]", msg.ok ? "text-ok" : "text-accent-ink")}>{msg.texto}</p>}
        </Card>
      )}

      {/* Cierres recientes (de esta botica) */}
      <Card>
        <h2 className="text-[13.5px] font-bold text-ink">Cierres recientes</h2>
        {!listo || historial.cargando || (!historial.data && !historial.error) ? (
          <p className="py-6 text-center text-[13px] text-ink-3">Cargando historial…</p>
        ) : cierres.length === 0 ? (
          <EmptyState
            className="mt-3"
            title="Aún no hay cierres"
            subtitle={puedeCerrar ? "Cierra la caja del día para verlo aquí." : "La caja la cierra el encargado. Aquí verás los cierres."}
          />
        ) : tarjetas ? (
          <ul className="mt-2 flex flex-col divide-y divide-line-row">
            {cierres.map((c) => {
              const contado = c.total_efectivo_cent + c.total_yape_cent + c.total_otros_cent;
              return (
                <li key={c.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-ink tabular-nums">{fechaCorta(c.fecha)}</p>
                    <p className="text-[12.5px] text-ink-2 tabular-nums">
                      Esperado {solesCent(c.total_sistema_cent)} · contado {solesCent(contado)}
                    </p>
                  </div>
                  <span className="flex-none">{resultadoCierre(c.diferencia_cent)}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <TableHead cols={COLS}>
              <Th>Fecha</Th>
              <Th align="right">Efectivo esperado</Th>
              <Th align="right">Efectivo contado</Th>
              <Th align="right">Yape</Th>
              <Th>Resultado</Th>
            </TableHead>
            {cierres.map((c) => {
              const contado = c.total_efectivo_cent + c.total_yape_cent + c.total_otros_cent;
              return (
                <TableRow key={c.id} cols={COLS}>
                  <Td className="text-[12.5px] text-ink-2">{c.fecha}</Td>
                  <Td align="right" className="tabular-nums text-[13px] text-ink">{solesCent(c.total_sistema_cent)}</Td>
                  <Td align="right" className="tabular-nums text-[13px] text-ink">{solesCent(contado)}</Td>
                  <Td align="right" className="tabular-nums text-[13px] text-ink-2">{solesCent(c.total_yape_cent)}</Td>
                  <Td>{resultadoCierre(c.diferencia_cent)}</Td>
                </TableRow>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function Campo({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="flex flex-col gap-1 text-[13px]">
      <span className="text-ink-2">{label}</span>
      <Input inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} placeholder="0.00" className="min-h-[44px] tabular-nums" />
    </label>
  );
}
